import { firstValueFrom } from "rxjs";
import { DashboardFilterSelectOption } from "./dashboard_filter_select_option";
import { SelectFilter, SingleSelectFilterSettings, SingleSelectFilterUISettings, SelectFilterInterface } from "./dashboard_select_filter";

class FilterState<V, PFV> {
  parentValue: PFV | null = null;
  value: V | null = null;
  selectedOption: DashboardFilterSelectOption | null = null;
}

export class DashboardPeriodSingleSelectFilterBase<FV, PFV = any> extends SelectFilter<FV, SingleSelectFilterSettings, SingleSelectFilterUISettings, DashboardFilterSelectOption, PFV> {

  private _previousState: FilterState<FV, PFV> = new FilterState();

  constructor(args: SelectFilterInterface<FV, SingleSelectFilterSettings, SingleSelectFilterUISettings, DashboardFilterSelectOption, PFV>) {

    if (args.initialValue == null && args.uiSettings.autoDisplayFirst && args.options.length > 0 && !args.parentFilterSettings?.parentFilterName) {
      args.initialValue = args.options[0][args.settings.optionValueKey] as FV;
    }

    super(args);
  }

  override isValueValid(value: FV): boolean {
    if (value == null) return true;

    const options = this._flatOptionsFilteredByParentFilter;
    return this.isValueValidInOptions(value, this._getFlatOptions(options));
  }

  override handleInvalidValue(value: FV) {
    const options = this._flatOptionsFilteredByParentFilter;
    if (!options.length) throw Error('Invalid Filter value and no options available');
    return this._getFlatOptions(options)[0][this.settings.optionValueKey] as FV;
  }

  override isValueValidInOptions(value: FV, flatOptions: DashboardFilterSelectOption[]): boolean {
    if (value == null) return true;
    return flatOptions.some(option => option[this.settings.optionValueKey] === value);
  }

  override onParentFilterValueChanged(parentValue: PFV, filteredOptions: DashboardFilterSelectOption[]) {
    let valueToSet;

    switch (true) {
      case filteredOptions.length === 0:
        valueToSet = null;
        break;
      case this.value == null && this.uiSettings.autoDisplayFirst:
        valueToSet = this._getFlatOptions(filteredOptions)[0][this.settings.optionValueKey] as FV;
        break;
      case this.isValueValidInOptions(this.value, filteredOptions):
        valueToSet = this.value;
        break;
      default:
        const optionsUnderLastParent = this.getOptionsUnderLastParent({
          filteredOptions,
          previousValue: this.value as string,
          parentValue: parentValue as string,
          previousParentValue: this._previousState.parentValue as string
        })
        valueToSet = optionsUnderLastParent[0][this.settings.optionValueKey];
    }

    this.setValue(valueToSet);

    this._previousState.parentValue = parentValue;

  }

  private getOptionsUnderLastParent(args: { filteredOptions: DashboardFilterSelectOption[], previousValue: string, parentValue: string, previousParentValue: string }) {
    const bridgeColumn = this.parentValueTransitionKeyMap[this.createTransitionKey(args.previousParentValue as string, args.parentValue as string)]
    const lastSelectedOption = this._flatBaseOptions.find(option => option[this.settings.optionValueKey] === args.previousValue);
    const options = args.filteredOptions.filter(option => option[bridgeColumn] === lastSelectedOption[bridgeColumn])
    return options;
  }

  // Stepping down: use previous key,
  // Stepping up: use later key
  private parentValueTransitionKeyMap: Record<string, string> = {
    'quarter_to_year': 'year_id',
    'month_to_year': 'year_id',
    'year_to_month': 'year_id',
    'year_to_quarter': 'year_id',
    'quarter_to_month': 'quarter_id',
    'month_to_quarter': 'quarter_id',
  }

  private createTransitionKey(prevParentValue: string, currentParentValue: string) {
    return `${prevParentValue}_to_${currentParentValue}`;
  }
}
